import * as React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Building2, Loader2 } from "lucide-react";
import { motion } from "motion/react";
import { ApiError, getCurrentTenant } from "@eduviaos/shared";
import { Button } from "@eduviaos/ui-kit/components/button";
import { Input } from "@eduviaos/ui-kit/components/input";
import { Label } from "@eduviaos/ui-kit/components/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@eduviaos/ui-kit/components/card";
import { useAuth } from "@/auth/AuthProvider";

/**
 * Login form. Tenant is taken from the URL subdomain
 * (`acme-school.eduviaos.app` → tenant=acme-school) so the user only
 * types email + password.
 *
 * Bare-host fallback: when the SPA is reached at `localhost:3001` (no
 * subdomain) we surface a small Institution slug field so devs can sign
 * in without spinning up `acme-school.localhost:3001`. In production
 * the tenant subdomain is always present so the field stays hidden.
 */
export function LoginForm() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const tenant = React.useMemo(() => getCurrentTenant(), []);

  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    const data = new FormData(e.currentTarget);
    const fallbackTenant = String(data.get("tenant_id") ?? "").trim() || undefined;
    try {
      await login({
        email: String(data.get("email") ?? "").trim(),
        password: String(data.get("password") ?? ""),
        tenant_id: tenant.slug ? undefined : fallbackTenant,
      });
      const from = (location.state as { from?: string } | null)?.from ?? "/dashboard";
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
    } finally {
      setPending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
    >
      <Card>
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
          <CardDescription>
            {tenant.slug ? (
              <span className="inline-flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
                <span>
                  Signing in to <span className="text-foreground font-medium">{tenant.label}</span>
                </span>
              </span>
            ) : (
              "Use the credentials issued by your institution administrator."
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form noValidate onSubmit={onSubmit} className="grid gap-4">
            {!tenant.slug ? (
              <div className="grid gap-2">
                <Label htmlFor="tenant_id">Institution ID</Label>
                <Input
                  id="tenant_id"
                  name="tenant_id"
                  autoComplete="organization"
                  required
                  placeholder="acme-school"
                  aria-describedby="tenant-hint"
                />
                <p id="tenant-hint" className="text-muted-foreground text-xs">
                  Tip: visit{" "}
                  <code className="bg-muted rounded px-1 py-0.5 text-[11px]">
                    your-school.localhost:3001
                  </code>{" "}
                  to skip this field.
                </p>
              </div>
            ) : null}
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder="you@example.com"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                minLength={1}
              />
            </div>
            {error ? (
              <p
                role="alert"
                className="border-destructive/30 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-sm"
              >
                {error}
              </p>
            ) : null}
            <Button type="submit" disabled={pending} className="mt-1">
              {pending ? (
                <>
                  <Loader2 className="animate-spin" aria-hidden="true" />
                  <span>Signing in…</span>
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </motion.div>
  );
}
